/* eslint-disable react/button-has-type */
import React, { CSSProperties, ReactNode } from 'react';
import ThreeDotsLoader from '../three-dots-loader/three-dots-loader';
import styles from './button-component.module.css';

interface ButtonComponentProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  style?: CSSProperties;
  variant?: 'primary' | 'secondary' | 'danger' | string;
  showLoading?: boolean;
  loaderColor?: string;
  className?: string;
  icon?: ReactNode;
  rightIcon?: ReactNode;
  disabledStyle?: CSSProperties;
  textStyle?: CSSProperties;
}

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonComponentProps>(
  (
    {
      children,
      onClick,
      type = 'button',
      disabled = false,
      style = {},
      variant = 'primary',
      showLoading = false,
      loaderColor = 'var(--white)',
      className = '',
      icon,
      rightIcon,
      disabledStyle = {},
      textStyle = {},
      ...rest
    },
    ref
  ) => {
    const clickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (showLoading) return;
      if (onClick) onClick(e);
    };

    const text = () => {
      if (!showLoading) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              alignItems: 'center',
              fontSize: '16px',
              fontWeight: '400',
              ...textStyle,
            }}
          >
            {icon}
            {children}
            {rightIcon}
          </div>
        );
      }
      return null;
    };

    const getButtonClassName = () => {
      const baseClass = styles.customButton;
      const variantClass = styles[`${variant}Button`];
      return `${baseClass} ${variantClass} ${className}`.trim();
    };

    if (disabled) {
      return (
        <button
          {...rest}
          className={getButtonClassName()}
          type="button"
          disabled
          style={{ ...style, ...disabledStyle }}
          ref={ref}
        >
          {icon}
          {children}
        </button>
      );
    }

    return (
      <button
        {...rest}
        className={getButtonClassName()}
        onClick={clickHandler}
        type={type}
        disabled={disabled}
        style={style}
        ref={ref}
      >
        {showLoading && <ThreeDotsLoader color={loaderColor} />}
        {text()}
      </button>
    );
  }
);

ButtonComponent.displayName = 'ButtonComponent';

export default ButtonComponent;
